import useSWR from "swr";
import { z } from "zod";
import { Network } from "@/utils/network";
import { zodUnsafeValidate } from "@/utils/zod";
import { InstagramSearch } from "../types";
import { useInstagramSearch } from "./useInstagramSearch";
import { useInstagramSearchForm } from "./useInstagramSearchForm";

const InstagramSearchCitiesResponseSchema = z.object({
  cities: InstagramSearch.OptionSchema.array(),
});

type InstagramSearchCitiesResponse = z.infer<
  typeof InstagramSearchCitiesResponseSchema
>;

export const useInstagramSearchCities = (query: string) => {
  const config = useInstagramSearch((state) => state.config);
  const country = useInstagramSearchForm((state) => state.country);
  const regions = useInstagramSearchForm((state) => state.regions);
  const setCities = useInstagramSearchForm((state) => state.setCities);

  return useSWR(
    () => {
      if (country === undefined) {
        return null;
      }

      return [config.api.getCities, country, regions, query];
    },
    async ([api, country, regions, query]) => {
      const { data } = await Network.get<InstagramSearchCitiesResponse>(api, {
        region: Array.from(regions),
        country: country,
        query,
      });

      return zodUnsafeValidate(InstagramSearchCitiesResponseSchema, data);
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: true,
      shouldRetryOnError: false,
      keepPreviousData: true,

      onSuccess: (data) => {
        const selection = useInstagramSearchForm.getState().cities;
        const selectedCities = data.cities
          .filter((city) => selection.has(city.id))
          .map((city) => city.id);

        setCities(selectedCities);
      },
    },
  );
};
